import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { getDatabase, ref, get } from 'firebase/database';
import { app } from '../firebase';
import {
  FaBriefcase, FaMapMarkerAlt, FaClock, FaBuilding,
  FaHeadset, FaHospital, FaLaptopCode, FaHotel,
  FaUniversity, FaGraduationCap, FaHardHat, FaShoppingCart,
  FaChevronRight, FaSearch, FaUsers, FaUtensils,
} from 'react-icons/fa';

// ─── Static data ───────────────────────────────────────────────

const PARISHES = [
  { slug: 'kingston',        label: 'Kingston',       desc: 'Corporate & government hub' },
  { slug: 'saint-andrew',    label: 'Saint Andrew',   desc: 'New Kingston business district' },
  { slug: 'montego-bay',     label: 'Montego Bay',    desc: 'BPO & resort capital' },
  { slug: 'saint-catherine', label: 'Saint Catherine', desc: 'Manufacturing & logistics' },
  { slug: 'portmore',        label: 'Portmore',       desc: 'Growing urban hub' },
  { slug: 'saint-james',     label: 'Saint James',    desc: 'Tourism & hospitality' },
  { slug: 'manchester',      label: 'Manchester',     desc: 'Services & retail' },
  { slug: 'saint-ann',       label: 'Saint Ann',      desc: 'Tourism & agriculture' },
  { slug: 'clarendon',       label: 'Clarendon',      desc: 'Mining & industry' },
  { slug: 'westmoreland',    label: 'Westmoreland',   desc: 'Tourism & fishing' },
  { slug: 'saint-elizabeth', label: 'Saint Elizabeth', desc: 'Agriculture & fishing' },
  { slug: 'saint-mary',      label: 'Saint Mary',     desc: 'Agriculture & tourism' },
  { slug: 'saint-thomas',    label: 'Saint Thomas',   desc: 'Agriculture & industry' },
  { slug: 'hanover',         label: 'Hanover',        desc: 'Tourism & agriculture' },
  { slug: 'trelawny',        label: 'Trelawny',       desc: 'Tourism & agriculture' },
  { slug: 'portland',        label: 'Portland',       desc: 'Eco-tourism & farming' },
];

const INDUSTRIES = [
  { slug: 'bpo',          label: 'BPO & Customer Service', Icon: FaHeadset,      color: '#6366f1' },
  { slug: 'healthcare',   label: 'Healthcare',              Icon: FaHospital,     color: '#ef4444' },
  { slug: 'technology',   label: 'IT & Technology',         Icon: FaLaptopCode,   color: '#1e3a5f' },
  { slug: 'tourism',      label: 'Tourism & Hospitality',   Icon: FaHotel,        color: '#f97316' },
  { slug: 'banking',      label: 'Banking & Finance',       Icon: FaUniversity,   color: '#16a34a' },
  { slug: 'education',    label: 'Education',               Icon: FaGraduationCap, color: '#7c3aed' },
  { slug: 'retail',       label: 'Retail & Sales',          Icon: FaShoppingCart, color: '#ec4899' },
  { slug: 'construction', label: 'Construction & Trades',   Icon: FaHardHat,      color: '#b45309' },
  { slug: 'food-restaurant', label: 'Food & Restaurant',    Icon: FaUtensils,     color: '#ca8a04' },
];

const JOB_TYPES = [
  { slug: 'full-time',   label: 'Full-Time' },
  { slug: 'part-time',   label: 'Part-Time' },
  { slug: 'remote',      label: 'Remote' },
  { slug: 'contract',    label: 'Contract' },
  { slug: 'entry-level', label: 'Entry Level' },
  { slug: 'internship',  label: 'Internship' },
];

const MARKET_SECTORS = [
  {
    title: 'BPO & Customer Service',
    body: 'Montego Bay and Kingston are two of the Caribbean\'s largest BPO hubs. Global companies employ thousands of Jamaicans in customer service, technical support, and back-office roles — making this one of the fastest-growing job sectors on the island.',
  },
  {
    title: 'Tourism & Hospitality',
    body: 'Jamaica welcomes over 4 million visitors annually. Hotels, resorts, restaurants, and tour operations across Saint James, Saint Ann, and Portland consistently hire for front-of-house, management, and support roles.',
  },
  {
    title: 'Healthcare',
    body: 'Nurses, doctors, pharmacists, and allied health professionals are in strong demand across both public and private sectors. The Ministry of Health and Wellness and major private hospitals regularly advertise vacancies island-wide.',
  },
  {
    title: 'Government & Public Sector',
    body: 'The Government of Jamaica is one of the largest employers on the island. Positions span ministries, statutory bodies, municipal corporations, and state-owned enterprises — from administrative roles to senior management.',
  },
  {
    title: 'IT & Technology',
    body: 'Kingston\'s growing tech ecosystem is creating opportunities in software development, digital marketing, data analytics, and fintech. Both local startups and multinational firms are actively hiring Jamaican tech talent.',
  },
  {
    title: 'Construction & Skilled Trades',
    body: 'Infrastructure development across Jamaica is driving demand for electricians, plumbers, carpenters, welders, and civil engineers. HEART/NSTA-certified tradespeople are particularly sought after by construction firms.',
  },
];

const FAQS = [
  {
    q: 'How do I find jobs in Jamaica?',
    a: 'The easiest way to find jobs in Jamaica is to search HireMeJA — Jamaica\'s dedicated job portal. Browse by parish (Kingston, Montego Bay, and all 14 parishes), industry, or job type. Create a free candidate profile to apply directly and receive alerts for new openings that match your skills.',
  },
  {
    q: 'What are the most in-demand jobs in Jamaica right now?',
    a: 'The most in-demand jobs in Jamaica include BPO and customer service agents, nurses and healthcare workers, IT and software professionals, teachers, hotel and hospitality staff, security guards, and skilled tradespeople such as electricians and plumbers.',
  },
  {
    q: 'What is the average salary in Jamaica?',
    a: 'Salaries in Jamaica vary by industry and experience. Entry-level roles typically start between JMD 60,000–100,000 per month. BPO and customer service positions average JMD 80,000–150,000. Skilled professionals in IT, banking, and healthcare can earn JMD 200,000–500,000+ monthly. Visit the HireMeJA Salary Guide for a full industry breakdown.',
  },
  {
    q: 'Where are most jobs in Jamaica located?',
    a: 'The majority of job vacancies in Jamaica are in Kingston and Saint Andrew (the commercial and government capital), Montego Bay in Saint James (the tourism and BPO hub), and Saint Catherine including Portmore. HireMeJA lists jobs across all 14 parishes.',
  },
  {
    q: 'Do I need a work permit to work in Jamaica?',
    a: 'Jamaican citizens and permanent residents do not need a work permit. Foreign nationals seeking employment in Jamaica generally require a work permit issued by the Ministry of Labour and Social Security. CARICOM nationals may qualify for exemptions under the CARICOM Single Market and Economy (CSME) agreement.',
  },
  {
    q: 'How do employers post jobs in Jamaica?',
    a: 'Employers can post jobs in Jamaica by registering on HireMeJA and selecting a subscription package. Packages include access to the candidate database, job listing slots, and direct applications. Jobs are reviewed and go live within 24 hours of submission.',
  },
];

// ─── Schema ────────────────────────────────────────────────────

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hiremeja.com' },
    { '@type': 'ListItem', position: 2, name: 'Jobs in Jamaica', item: 'https://hiremeja.com/jobs-in-jamaica' },
  ],
};

// ─── Sub-components ────────────────────────────────────────────

const SectionHeading = ({ label, sub }) => (
  <div className="mb-8 text-center">
    <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#1e3a5f' }}>{label}</h2>
    {sub && <p className="text-gray-500 mt-2 text-sm sm:text-base">{sub}</p>}
  </div>
);

const JobCard = ({ job }) => {
  const posted = job.approvedAt
    ? new Date(job.approvedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <Link
      to={`/jobs/${job.slug || job._key}`}
      className="block bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-950 hover:shadow-md transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <FaBuilding className="text-blue-950 w-5 h-5" />
        </div>
        {job.employmentType && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#cddd3a]/20 text-blue-950 shrink-0">
            {job.employmentType}
          </span>
        )}
      </div>
      <h3 className="font-bold text-blue-950 mt-3 text-sm sm:text-base group-hover:text-blue-700 transition-colors line-clamp-2">
        {job.jobTitle}
      </h3>
      <p className="text-sm text-gray-500 mt-1">{job.companyName}</p>
      <div className="flex flex-wrap gap-3 mt-3">
        {job.parish && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <FaMapMarkerAlt size={10} /> {job.parish}
          </span>
        )}
        {posted && (
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <FaClock size={10} /> {posted}
          </span>
        )}
      </div>
    </Link>
  );
};

// ─── Main component ────────────────────────────────────────────

const JobsInJamaica = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [jobCount, setJobCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getDatabase(app);
    get(ref(db, 'jobs')).then((snapshot) => {
      if (!snapshot.exists()) { setLoading(false); return; }
      const all = [];
      snapshot.forEach((empSnap) => {
        empSnap.forEach((jobSnap) => {
          const job = jobSnap.val();
          if (job.status === 'approved' || job.status === 'active') {
            all.push({ ...job, _key: jobSnap.key });
          }
        });
      });
      all.sort((a, b) =>
        (b.approvedAt || b.createdAt || 0) - (a.approvedAt || a.createdAt || 0)
      );
      setJobCount(all.length);
      setJobs(all.slice(0, 9));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Jobs in Jamaica — Search &amp; Apply for Jamaica Jobs | HireMeJA</title>
        <meta
          name="description"
          content="Find jobs in Jamaica across all 14 parishes. Browse hundreds of job vacancies in Kingston, Montego Bay, and beyond on Jamaica's premier job portal — HireMeJA."
        />
        <meta
          name="keywords"
          content="jobs in Jamaica, Jamaica jobs, job search Jamaica, job portal Jamaica, find jobs Jamaica, employment Jamaica, jobs Kingston, jobs Montego Bay"
        />
        <link rel="canonical" href="https://hiremeja.com/jobs-in-jamaica" />
        <meta property="og:title" content="Jobs in Jamaica — Search & Apply | HireMeJA" />
        <meta property="og:description" content="Browse hundreds of job vacancies across all 14 parishes. Jamaica's premier job portal." />
        <meta property="og:url" content="https://hiremeja.com/jobs-in-jamaica" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* ── Hero ──────────────────────────────────────────────── */}
      <header className="py-16 sm:py-24" style={{ background: '#1e3a5f' }}>
        <div className="container px-4 mx-auto text-center max-w-3xl">
          {/* Breadcrumb */}
          <p className="text-xs font-medium mb-4" style={{ color: '#93c5fd' }}>
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2 opacity-50">/</span>
            <span>Jobs in Jamaica</span>
          </p>

          <h1 className="text-3xl sm:text-5xl font-black text-white leading-tight">
            Jobs in Jamaica
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: '#bfdbfe' }}>
            Browse{' '}
            {jobCount != null ? (
              <strong className="text-white">{jobCount} active job{jobCount !== 1 ? 's' : ''}</strong>
            ) : 'hundreds of job vacancies'}
            {' '}across all 14 parishes. Jamaica's premier job portal — trusted by employers and job seekers island-wide.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <button
              onClick={() => navigate('/jobs')}
              className="flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-lg transition-opacity hover:opacity-90"
              style={{ background: '#cddd3a', color: '#1e3a5f' }}
            >
              <FaSearch size={14} /> Search All Jobs
            </button>
            <button
              onClick={() => navigate('/candidate-registration')}
              className="flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-lg border-2 text-white transition-colors hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}
            >
              Create Free Profile
            </button>
          </div>

          {/* Quick stats */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 pt-8"
            style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            {[
              { icon: FaBriefcase, label: jobCount != null ? `${jobCount} Active Jobs` : 'Active Jobs' },
              { icon: FaUsers,     label: '500+ Candidates' },
              { icon: FaMapMarkerAlt, label: '14 Parishes' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon size={14} style={{ color: '#cddd3a' }} />
                <span className="text-sm font-semibold text-white">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="bg-gray-50">

        {/* ── Latest Jobs ───────────────────────────────────────── */}
        <section className="py-14 sm:py-20">
          <div className="container px-4 mx-auto">
            <SectionHeading
              label="Latest Jobs in Jamaica"
              sub="Live vacancies updated daily — approved by our team"
            />
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: '#1e3a5f', borderTopColor: 'transparent' }} />
              </div>
            ) : jobs.length === 0 ? (
              <p className="text-center text-gray-400 py-10">No jobs available right now. Check back soon.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {jobs.map((job) => (
                  <JobCard key={job._key} job={job} />
                ))}
              </div>
            )}
            <div className="text-center mt-8">
              <Link
                to="/jobs"
                className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-lg text-white transition-opacity hover:opacity-90"
                style={{ background: '#1e3a5f' }}
              >
                View All Jobs <FaChevronRight size={12} />
              </Link>
            </div>
          </div>
        </section>

        {/* ── Browse by Parish ──────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="container px-4 mx-auto">
            <SectionHeading
              label="Find Jobs Near You"
              sub="Browse job vacancies by parish across all of Jamaica"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {PARISHES.map(({ slug, label, desc }) => (
                <Link
                  key={slug}
                  to={`/jobs/location/${slug}`}
                  className="group flex flex-col p-4 rounded-xl border border-gray-200 bg-gray-50 hover:border-blue-950 hover:bg-blue-50 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt size={12} style={{ color: '#cddd3a' }} className="shrink-0" />
                    <span className="font-semibold text-sm text-blue-950 group-hover:text-blue-800">
                      {label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 ml-4">{desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Browse by Industry ────────────────────────────────── */}
        <section className="py-14 sm:py-20">
          <div className="container px-4 mx-auto">
            <SectionHeading
              label="Jobs by Industry"
              sub="Find your next role in Jamaica's key employment sectors"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
              {INDUSTRIES.map(({ slug, label, Icon, color }) => (
                <Link
                  key={slug}
                  to={`/jobs/industry/${slug}`}
                  className="group flex items-center gap-4 p-5 rounded-xl bg-white border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${color}18` }}>
                    <Icon size={18} style={{ color }} />
                  </div>
                  <span className="font-semibold text-sm text-blue-950 group-hover:text-blue-700 transition-colors">
                    {label}
                  </span>
                  <FaChevronRight size={10} className="ml-auto text-gray-300 group-hover:text-blue-950 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── Browse by Type ────────────────────────────────────── */}
        <section className="py-10 bg-white">
          <div className="container px-4 mx-auto">
            <h3 className="text-center font-bold text-blue-950 mb-5">Browse by Job Type</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {JOB_TYPES.map(({ slug, label }) => (
                <Link
                  key={slug}
                  to={`/jobs/type/${slug}`}
                  className="px-5 py-2 rounded-full border font-semibold text-sm transition-colors hover:text-white"
                  style={{ borderColor: '#1e3a5f', color: '#1e3a5f' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#1e3a5f';
                    e.currentTarget.style.color = '#cddd3a';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1e3a5f';
                  }}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── About Jamaica's Job Market ───────────────────────── */}
        <section className="py-14 sm:py-20">
          <div className="container px-4 mx-auto max-w-5xl">
            <SectionHeading
              label="About Jamaica's Job Market"
              sub="Key employment sectors and opportunities across the island"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {MARKET_SECTORS.map(({ title, body }) => (
                <div key={title} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="w-1 h-8 rounded-full mb-4" style={{ background: '#cddd3a' }} />
                  <h3 className="font-bold text-blue-950 mb-2">{title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-xl p-6 sm:p-8 text-sm text-gray-600 leading-relaxed"
              style={{ background: '#f0f7ff', border: '1px solid #bfdbfe' }}>
              <strong className="text-blue-950 block mb-2">About HireMeJA</strong>
              HireMeJA is Jamaica's dedicated online job board, built specifically for the Jamaican labour market.
              Unlike global platforms, every employer and job listing on HireMeJA is reviewed and approved —
              giving job seekers confidence that opportunities are genuine. Employers can post jobs, search
              hundreds of candidate profiles, and send direct invitations to qualified applicants.
              Job seekers register free, build a searchable profile, and apply to multiple roles in minutes.
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="container px-4 mx-auto max-w-3xl">
            <SectionHeading
              label="Frequently Asked Questions"
              sub="Everything you need to know about finding jobs in Jamaica"
            />
            <div className="space-y-4">
              {FAQS.map(({ q, a }) => (
                <details
                  key={q}
                  className="group rounded-xl border border-gray-200 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-semibold text-blue-950 text-sm sm:text-base list-none hover:bg-blue-50 transition-colors">
                    {q}
                    <FaChevronRight size={12} className="shrink-0 ml-3 text-gray-400 group-open:rotate-90 transition-transform duration-200" />
                  </summary>
                  <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────── */}
        <section className="py-14 sm:py-20" style={{ background: '#1e3a5f' }}>
          <div className="container px-4 mx-auto max-w-4xl">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                Ready to Get Started?
              </h2>
              <p className="mt-2 text-sm sm:text-base" style={{ color: '#bfdbfe' }}>
                Whether you're looking for work or looking to hire — HireMeJA is the fastest way to connect.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="rounded-xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#cddd3a' }}>
                  <FaSearch size={18} style={{ color: '#1e3a5f' }} />
                </div>
                <h3 className="font-bold text-white mb-1">Find a Job</h3>
                <p className="text-sm mb-4" style={{ color: '#93c5fd' }}>
                  Create a free profile and apply to jobs across Jamaica.
                </p>
                <button
                  onClick={() => navigate('/candidate-registration')}
                  className="w-full py-2.5 rounded-lg font-bold text-sm transition-opacity hover:opacity-90"
                  style={{ background: '#cddd3a', color: '#1e3a5f' }}
                >
                  Get Hired
                </button>
              </div>
              <div className="rounded-xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-white">
                  <FaBuilding size={18} style={{ color: '#1e3a5f' }} />
                </div>
                <h3 className="font-bold text-white mb-1">Hire Talent</h3>
                <p className="text-sm mb-4" style={{ color: '#93c5fd' }}>
                  Post jobs and search our database of Jamaican candidates.
                </p>
                <button
                  onClick={() => navigate('/employer-registration')}
                  className="w-full py-2.5 rounded-lg font-bold text-sm border-2 text-white transition-colors hover:bg-white/10"
                  style={{ borderColor: 'rgba(255,255,255,0.4)' }}
                >
                  Post a Job
                </button>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default JobsInJamaica;
